import { formatPhoneNumberForDisplay } from '@/lib/phone-number';
import { cn } from '@/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import type { CountryCode } from 'libphonenumber-js';
import { useEffect, useState } from 'react';

type ContactSublineContact = {
  name?: string;
  phoneNumber?: string;
  companyName?: string;
  jobTitle?: string;
};

type ContactSublineProps = {
  contact?: ContactSublineContact;
  lineCountry?: CountryCode;
  centered?: boolean;
  paused?: boolean;
  className?: string;
};

const CYCLE_INTERVAL = 3500;

export const ContactSubline = ({
  contact,
  lineCountry,
  centered,
  paused,
  className = '',
}: ContactSublineProps) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const contactDisplayNumber = contact?.phoneNumber
    ? formatPhoneNumberForDisplay(contact.phoneNumber, lineCountry)
    : undefined;

  const showPhoneNumber = !!(
    contact?.name &&
    contact.name !== contact?.phoneNumber &&
    contactDisplayNumber
  );

  const items: string[] = [
    ...(contact?.companyName ? [contact.companyName] : []),
    ...(contact?.jobTitle ? [contact.jobTitle] : []),
    ...(showPhoneNumber ? [contactDisplayNumber!] : []),
  ];

  useEffect(() => {
    setActiveIndex(0);
  }, [items.length]);

  useEffect(() => {
    if (items.length <= 1 || paused) {
      return;
    }

    const interval = setInterval(() => {
      setActiveIndex((i) => (i + 1) % items.length);
    }, CYCLE_INTERVAL);

    return () => clearInterval(interval);
  }, [items.length, paused]);

  const spanClass = cn(
    'text-body-14 text-primary-60 line-clamp-1 break-all',
    centered && 'text-center',
    className,
  );

  if (items.length === 0) {
    return null;
  }

  if (items.length === 1) {
    return <span className={spanClass}>{items[0]}</span>;
  }

  return (
    <div className="relative w-full overflow-hidden" style={{ height: '1.25em' }}>
      <AnimatePresence mode="popLayout">
        <motion.span
          key={activeIndex}
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '-100%', opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeInOut' }}
          className={cn('absolute inset-0 block', spanClass)}
        >
          {items[activeIndex % items.length]}
        </motion.span>
      </AnimatePresence>
    </div>
  );
};
